import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchChildWhatsAppPreferences,
  saveChildWhatsAppPreferences,
} from '../../api/studentPortal';
import { usePortalStudentId } from '../../hooks/usePortalStudentId';
import ParentPortalHeader from '../../components/parent/ParentPortalHeader';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import WhatsAppUnavailableNotice from '../../components/notifications/WhatsAppUnavailableNotice';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';

const TYPE_TOGGLE_KEYS = [
  { key: 'sessionReminders', labelKey: 'pages.notifications.sessionReminders' },
  { key: 'absenceAlerts', labelKey: 'pages.notifications.absenceAlerts' },
  { key: 'lowBalance', labelKey: 'pages.notifications.lowBalance' },
  { key: 'feeReminders', labelKey: 'pages.notifications.feeReminders' },
  { key: 'sessionCancelled', labelKey: 'pages.notifications.sessionCancelled' },
  { key: 'classroomStarted', labelKey: 'pages.notifications.classroomStarted' },
  { key: 'holidayAnnouncements', labelKey: 'pages.notifications.holidayAnnouncements' },
];

export default function ParentChildNotifications() {
  const { t } = useTranslation();
  const childStudentId = usePortalStudentId();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const typeToggles = useMemo(
    () => TYPE_TOGGLE_KEYS.map((item) => ({ ...item, label: t(item.labelKey) })),
    [t],
  );

  useEffect(() => {
    if (!childStudentId) return;
    setLoading(true);
    setPrefs(null);
    setErr('');
    fetchChildWhatsAppPreferences(childStudentId)
      .then(setPrefs)
      .catch(() => {
        setPrefs(null);
        setErr(t('pages.notifications.loadFailed'));
      })
      .finally(() => setLoading(false));
  }, [childStudentId, t]);

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const persist = async () => {
    const saved = await saveChildWhatsAppPreferences(childStudentId, prefs);
    setPrefs(saved);
    return saved;
  };
  const { markSaved } = useUnsavedChanges(prefs, {
    enabled: !!prefs,
    onSave: async () => { await persist(); },
  });

  const save = async () => {
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const saved = await persist();
      markSaved(saved);
      setMsg(t('pages.parentNotifications.saved'));
    } catch {
      setErr(t('pages.notifications.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('pages.parentNotifications.title')}
        subtitle={t('pages.parentNotifications.subtitle')}
        showBack={false}
      />
    );
  }
  if (!prefs) return <div className="page-shell p-6 text-red-700">{err || t('pages.notifications.unavailable')}</div>;

  const available = prefs.licenseWhatsAppEnabled && prefs.academyWhatsAppEnabled;

  return (
    <div className="page-shell max-w-2xl mx-auto space-y-6">
      <ParentPortalHeader />
      <PageHeaderSection
        title={t('pages.parentNotifications.title')}
        subtitle={t('pages.parentNotifications.subtitle')}
      />

      {!available && <WhatsAppUnavailableNotice prefs={prefs} forParent />}

      <div className={`bg-white rounded-xl border p-4 md:p-6 space-y-4 ${!available ? 'opacity-60' : ''}`}>
        <label className="flex items-start gap-3 py-2 border-b">
          <input
            type="checkbox"
            className="h-5 w-5 mt-0.5"
            checked={!!prefs.whatsappOptIn}
            disabled={!available}
            onChange={() => toggle('whatsappOptIn')}
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">{t('pages.parentNotifications.optInTitle')}</span>
            <span className="block text-xs text-gray-500 mt-1">{t('pages.parentNotifications.optInHint')}</span>
          </span>
        </label>

        {typeToggles.map(({ key, label }) => (
          <label key={key} className="flex items-center justify-between gap-4 py-2 border-b last:border-0">
            <span className="text-sm text-gray-800">{label}</span>
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={!!prefs[key]}
              disabled={!available || !prefs.whatsappOptIn}
              onChange={() => toggle(key)}
            />
          </label>
        ))}

        {available && (
          <>
            {err && <p className="text-sm text-red-600">{err}</p>}
            {msg && <p className="text-sm text-green-600">{msg}</p>}

            <Button type="button" variant="primary" disabled={saving} onClick={save}>
              {saving ? t('pages.notifications.saving') : t('pages.notifications.savePreferences')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
